/**
 * inventoryBulkActions — single source of truth for what bulk actions
 * exist on the selection bar and how they behave.
 *
 * Same registry pattern as FILTER_AXES — adding a new bulk action is
 * one entry here. The BulkActionBar reads from this and renders the
 * action buttons + popover-or-handler dispatch.
 *
 * Each action has one of two shapes:
 *
 *   { kind: 'direct', handler(ctx) → Promise<{label}> }
 *     For one-shot actions (Reprocess, Index, Apply updates, Chat scope).
 *
 *   { kind: 'popover', Popover: <Component> }
 *     For multi-step actions (Move, Reclassify, Delete, Deep search).
 *     Popover receives { caseId, ids, count, folderPaths, anchorRef,
 *     onClose, onCompleted }.
 *
 * Shared fields:
 *   key            — stable id (matches BULK_ACTIONS object key)
 *   label          — button text
 *   icon           — emoji prefix
 *   variant        — 'primary' | 'secondary' | 'destructive'
 *   surface        — 'inline' | 'more' | 'inline-right'
 *   isApplicable   — (docs, ctx) => bool; gates whether action is shown
 */

import { api } from '../api.js';
import MovePopover from '../components/inventory/bulk_actions/MovePopover';
import CopyPopover from '../components/inventory/bulk_actions/CopyPopover';
import ExportPopover from '../components/inventory/bulk_actions/ExportPopover';
import DeleteConfirmPopover from '../components/inventory/bulk_actions/DeleteConfirmPopover';
import ReclassifyPopover from '../components/inventory/bulk_actions/ReclassifyPopover';
import DeepSearchPopover from '../components/inventory/bulk_actions/DeepSearchPopover';

export const BULK_ACTIONS = {
  move: {
    key: 'move',
    label: 'Move',
    icon: '📁',
    variant: 'primary',
    surface: 'inline',
    kind: 'popover',
    Popover: MovePopover,
  },
  copy: {
    key: 'copy',
    label: 'Copy to folder',
    icon: '📋',
    variant: 'primary',
    surface: 'inline',
    kind: 'popover',
    Popover: CopyPopover,
  },
  reprocess: {
    key: 'reprocess',
    label: 'Reprocess',
    icon: '🔄',
    variant: 'primary',
    surface: 'inline',
    kind: 'direct',
    async handler({ caseId, ids, count, onDocChange }) {
      let ok = 0, fail = 0;
      const promises = ids.map((id) =>
        api.post(`/processing/documents/${id}/reprocess`, {})
          .then(() => { ok++; })
          .catch(() => { fail++; })
      );
      // Delay refresh so backend has time to commit status=PROCESSING.
      setTimeout(() => onDocChange?.(), 400);
      await Promise.all(promises);
      onDocChange?.();
      const label = fail === 0
        ? `Reprocessed ${ok} file${ok === 1 ? '' : 's'}`
        : `Reprocessed ${ok}, failed ${fail}`;
      return { label, error: fail > 0 };
    },
  },
  reclassify: {
    key: 'reclassify',
    label: 'Reclassify',
    icon: '🏷️',
    variant: 'primary',
    surface: 'inline',
    kind: 'popover',
    Popover: ReclassifyPopover,
  },
  // 'Mark Reviewed' is gated by isApplicable — only shown when the
  // selection contains corrected (human_manual) docs. Endpoint is
  // per-doc ack; the handler fans out.
  markReviewed: {
    key: 'markReviewed',
    label: 'Mark Reviewed',
    icon: '✓',
    variant: 'primary',
    surface: 'inline',
    kind: 'direct',
    isApplicable: (docs) => docs.some((d) => d.classification_source === 'human_manual'),
    async handler({ ids, documents, onDocChange }) {
      // Only ack the corrected docs in the selection; ignore others.
      const correctedIds = ids.filter((id) => {
        const d = documents.find((x) => x.id === id);
        return d?.classification_source === 'human_manual';
      });
      let ok = 0, fail = 0;
      await Promise.all(correctedIds.map((id) =>
        api.post(`/documents/${id}/correction-ack`, {})
          .then(() => { ok++; })
          .catch(() => { fail++; })
      ));
      onDocChange?.();
      return {
        label: fail === 0
          ? `Marked ${ok} correction${ok === 1 ? '' : 's'} reviewed`
          : `Acked ${ok}, failed ${fail}`,
        error: fail > 0,
      };
    },
  },
  export: {
    key: 'export',
    label: 'Export',
    icon: '⬇️',
    variant: 'secondary',
    surface: 'more',
    kind: 'popover',
    Popover: ExportPopover,
  },
  indexForSearch: {
    key: 'indexForSearch',
    label: 'Index for search',
    icon: '🔍',
    variant: 'secondary',
    surface: 'more',
    kind: 'direct',
    // Redacted copies are never indexed — hide if any selection is redacted.
    isApplicable: (docs) => !docs.some((d) => d.is_redacted),
    async handler({ caseId, ids, count }) {
      await api.post(`/exploration/case/${caseId}/bulk-index`, {
        document_ids: ids,
        complete_extraction: true,
      });
      return { label: `Indexing ${count} file${count === 1 ? '' : 's'}` };
    },
  },
  deepSearch: {
    key: 'deepSearch',
    label: 'Upgrade to Deep search',
    icon: '🔬',
    variant: 'secondary',
    surface: 'more',
    kind: 'popover',
    Popover: DeepSearchPopover,
    // Redacted copies are never indexed — hide if any selection is redacted.
    isApplicable: (docs) => !docs.some((d) => d.is_redacted),
  },
  applyUpdates: {
    key: 'applyUpdates',
    label: 'Apply pending updates',
    icon: '✨',
    variant: 'secondary',
    surface: 'more',
    kind: 'direct',
    async handler({ caseId, ids, count }) {
      await api.post(`/cases/${caseId}/stages/backfill`, {
        classes: ['analyze'],
        document_ids: ids,
      });
      return { label: `Applied updates to ${count} file${count === 1 ? '' : 's'}` };
    },
  },
  chatScope: {
    key: 'chatScope',
    label: 'Set chat scope',
    icon: '💬',
    variant: 'secondary',
    surface: 'more',
    kind: 'direct',
    isApplicable: (docs, ctx) => Boolean(ctx?.onChatScope),
    async handler({ ids, count, onChatScope }) {
      onChatScope?.(ids);
      return { label: `Chat scope set to ${count} file${count === 1 ? '' : 's'}` };
    },
  },
  useAsChecklistMatch: {
    key: 'useAsChecklistMatch',
    label: 'Use as match',
    icon: '📎',
    variant: 'primary',
    surface: 'inline',
    kind: 'direct',
    isApplicable: (docs, ctx) =>
      ctx?.mode?.key === 'matchChecklistItem' && docs.length === 1,
    async handler({ ids, caseId, documents, modeContext, onMatchRecorded }) {
      const doc = documents.find((d) => d.id === ids[0]) || null;
      await api.patch(`/cases/${caseId}/checklist/items/batch`, {
        updates: [{
          item_id: modeContext.itemId,
          status: 'matched',
          source_document_id: ids[0],
          file_path: doc?.relative_path || doc?.file_path || null,
          manually_verified: true,
          match_method: 'manual_pick',
        }],
      });
      onMatchRecorded?.(modeContext.itemId);
      return { label: 'Matched.' };
    },
  },
  delete: {
    key: 'delete',
    label: 'Delete',
    icon: '🗑️',
    variant: 'destructive',
    surface: 'inline-right',
    kind: 'popover',
    Popover: DeleteConfirmPopover,
  },
};

/** Stable order for rendering. */
export const BULK_ACTION_ORDER = [
  'useAsChecklistMatch',
  'move', 'copy', 'reprocess', 'reclassify', 'markReviewed',
  'indexForSearch', 'export', 'deepSearch', 'applyUpdates', 'chatScope',
  'delete',
];

/** Get the actions that should currently render, partitioned by surface. */
export function getVisibleActions(docs, ctx) {
  const visible = BULK_ACTION_ORDER
    .map((k) => BULK_ACTIONS[k])
    .filter((a) => a)
    .filter((a) => !a.isApplicable || a.isApplicable(docs, ctx));

  return {
    inline: visible.filter((a) => a.surface === 'inline'),
    more: visible.filter((a) => a.surface === 'more'),
    inlineRight: visible.filter((a) => a.surface === 'inline-right'),
  };
}
